/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { queryOptions } from '@tanstack/react-query';
import type { Credentials } from '@opengeoweb/authentication';
import {
  getTemplatePresets,
  getTemplatePresetsFilename,
  getWorkspacePreset,
  getWorkspacePresets,
  saveWorkspacePreset,
  saveWorkspacePresetAs,
} from './api';
import type {
  TemplatePresetsResponse,
  WorkspacePresetForBE,
  WorkspacePresetFromBE,
  WorkspacePresetListItem,
} from '../../store/workspace/types';
import { LIB_KEY } from '../utils';

const API_KEY = 'workspace-api';

const generateWorkspaceQueryKeys = (...queryKeys: string[]): string[] => [
  LIB_KEY,
  API_KEY,
  ...queryKeys,
];

export const workspaceQueryOptions = {
  keys: {
    all: () => generateWorkspaceQueryKeys(),
    workspaces: () => [...workspaceQueryOptions.keys.all(), 'workspace'],
    lists: () => [...workspaceQueryOptions.keys.all(), 'list'],
  },
  getTemplatePresets: (filename: string = getTemplatePresetsFilename()) =>
    queryOptions({
      queryKey: generateWorkspaceQueryKeys('templates', filename),
      queryFn: async (): Promise<TemplatePresetsResponse> =>
        getTemplatePresets(filename),
      staleTime: Infinity,
      // A missing static file will not appear by retrying, and opening a
      // workspace waits for this query
      retry: false,
    }),
  getWorkspacePreset: (id: string, credentials?: Credentials) =>
    queryOptions({
      queryKey: generateWorkspaceQueryKeys(id),
      queryFn: async (): Promise<WorkspacePresetFromBE> =>
        getWorkspacePreset(id, credentials),
    }),
  getWorkspacePresets: (credentials?: Credentials) =>
    queryOptions({
      queryKey: workspaceQueryOptions.keys.lists(),
      queryFn: async (): Promise<WorkspacePresetListItem[]> =>
        getWorkspacePresets(credentials),
      staleTime: 2 * 60 * 1000,
      refetchOnMount: true,
    }),
  saveWorkspacePreset: (
    id: string,
    data: WorkspacePresetForBE,
    credentials?: Credentials,
  ) =>
    queryOptions({
      queryKey: generateWorkspaceQueryKeys(id),
      staleTime: 0,
      queryFn: async (): Promise<void> =>
        saveWorkspacePreset(id, data, credentials),
    }),
  saveWorkspacePresetAs: (
    data: WorkspacePresetForBE,
    credentials?: Credentials,
  ) =>
    queryOptions({
      queryKey: generateWorkspaceQueryKeys('save-as'),
      staleTime: 0,
      queryFn: async (): Promise<string> =>
        saveWorkspacePresetAs(data, credentials),
    }),
} as const;
